import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

// ─── Category-specific field configs ────────────────────────────────────────

const CATEGORY_CONFIGS = {
  'vintage-watches': {
    label: 'Watch Details',
    fields: [
      { key: 'brand', label: 'Brand / Manufacturer', placeholder: 'e.g. Rolex, Omega, Patek Philippe' },
      { key: 'model', label: 'Model / Reference', placeholder: 'e.g. Submariner 1680' },
      { key: 'movement_type', label: 'Movement Type', type: 'select', options: ['Automatic', 'Manual Wind', 'Quartz', 'Tourbillon', 'Other'] },
      { key: 'case_material', label: 'Case Material', placeholder: 'e.g. Stainless Steel, Gold, Titanium' },
      { key: 'dial_color', label: 'Dial Color', placeholder: 'e.g. Black, Silver, Blue' },
      { key: 'case_size_mm', label: 'Case Size (mm)', placeholder: 'e.g. 40', inputMode: 'decimal' },
      { key: 'serial_number', label: 'Serial Number', placeholder: 'Watch serial number' },
      { key: 'box_papers', label: 'Box & Papers', type: 'select', options: ['Full Set (Box + Papers)', 'Box Only', 'Papers Only', 'No Box or Papers'] },
    ]
  },
  'sports-cards': {
    label: 'Card Details',
    fields: [
      { key: 'player_name', label: 'Player Name', placeholder: 'e.g. Michael Jordan' },
      { key: 'sport', label: 'Sport', placeholder: 'e.g. Basketball, Baseball, Football' },
      { key: 'team', label: 'Team', placeholder: 'e.g. Chicago Bulls' },
      { key: 'card_set', label: 'Card Set / Series', placeholder: 'e.g. 1986-87 Fleer' },
      { key: 'card_number', label: 'Card Number', placeholder: 'e.g. #57' },
      { key: 'is_rookie_card', label: 'Rookie Card', type: 'select', options: ['Yes', 'No'] },
      { key: 'grading_company', label: 'Grading Company', placeholder: 'e.g. PSA, BGS, SGC' },
      { key: 'grade', label: 'Grade', placeholder: 'e.g. PSA 10, BGS 9.5' },
      { key: 'print_run', label: 'Print Run / Numbered', placeholder: 'e.g. /25, /100' },
    ]
  },
  'comics': {
    label: 'Comic Details',
    fields: [
      { key: 'publisher', label: 'Publisher', placeholder: 'e.g. Marvel, DC, Image' },
      { key: 'series_title', label: 'Series Title', placeholder: 'e.g. Amazing Spider-Man' },
      { key: 'issue_number', label: 'Issue Number', placeholder: 'e.g. #1, #300' },
      { key: 'cover_date', label: 'Cover Date', placeholder: 'e.g. June 1962' },
      { key: 'key_issue_notes', label: 'Key Issue Notes', placeholder: 'e.g. First appearance of Spider-Man' },
      { key: 'variant_cover', label: 'Variant Cover', type: 'select', options: ['Standard', 'Variant A', 'Variant B', 'Newsstand', 'Direct Edition', 'Other'] },
      { key: 'grading_company', label: 'Grading Company', placeholder: 'e.g. CGC, CBCS' },
      { key: 'grade', label: 'Grade', placeholder: 'e.g. CGC 9.8' },
      { key: 'signed', label: 'Signed', type: 'select', options: ['No', 'Yes – Unwitnessed', 'Yes – Witnessed / Certified'] },
    ]
  },
  'art-prints': {
    label: 'Artwork Details',
    fields: [
      { key: 'artist', label: 'Artist', placeholder: 'e.g. Andy Warhol' },
      { key: 'medium', label: 'Medium', placeholder: 'e.g. Lithograph, Screen Print, Giclée' },
      { key: 'edition_size', label: 'Edition Size', placeholder: 'e.g. 50/200' },
      { key: 'dimensions', label: 'Dimensions', placeholder: 'e.g. 18" × 24"' },
      { key: 'framed', label: 'Framed', type: 'select', options: ['Unframed', 'Framed', 'Canvas Stretched'] },
      { key: 'certificate_of_authenticity', label: 'Certificate of Authenticity', type: 'select', options: ['Yes', 'No'] },
      { key: 'provenance', label: 'Provenance / Gallery', placeholder: 'e.g. Purchased from Gagosian Gallery' },
      { key: 'signed', label: 'Signed', type: 'select', options: ['No', 'Yes – Signed', 'Yes – Signed & Numbered'] },
    ]
  },
  'stamps': {
    label: 'Stamp Details',
    fields: [
      { key: 'country', label: 'Country of Origin', placeholder: 'e.g. United States, Great Britain' },
      { key: 'scott_number', label: 'Scott / Catalogue Number', placeholder: 'e.g. Scott #1' },
      { key: 'issue_date', label: 'Issue Date / Year', placeholder: 'e.g. 1847' },
      { key: 'denomination', label: 'Denomination', placeholder: 'e.g. 5¢, 1 Penny' },
      { key: 'color', label: 'Color', placeholder: 'e.g. Blue, Carmine' },
      { key: 'perforation', label: 'Perforation', placeholder: 'e.g. Perf 11, Imperf' },
      { key: 'gum_status', label: 'Gum Status', type: 'select', options: ['Mint NH (Never Hinged)', 'Mint OG (Original Gum)', 'Hinged', 'No Gum', 'Used'] },
      { key: 'centering', label: 'Centering', type: 'select', options: ['Superb', 'Extremely Fine', 'Very Fine', 'Fine', 'Average', 'Poor'] },
    ]
  },
  'rare-coins': {
    label: 'Coin Details',
    fields: [
      { key: 'country', label: 'Country', placeholder: 'e.g. United States, Roman Empire' },
      { key: 'denomination', label: 'Denomination', placeholder: 'e.g. Morgan Dollar, Penny' },
      { key: 'mint_mark', label: 'Mint Mark', placeholder: 'e.g. S, D, CC, No Mark' },
      { key: 'composition', label: 'Composition', placeholder: 'e.g. 90% Silver, Gold, Copper' },
      { key: 'diameter_mm', label: 'Diameter (mm)', placeholder: 'e.g. 38.1', inputMode: 'decimal' },
      { key: 'grading_company', label: 'Grading Company', placeholder: 'e.g. PCGS, NGC, ANACS' },
      { key: 'grade', label: 'Grade', placeholder: 'e.g. MS-65, PR-70' },
      { key: 'certification_number', label: 'Certification Number', placeholder: 'Slab cert number' },
      { key: 'variety', label: 'Variety / VAM', placeholder: 'e.g. VAM-4, Doubled Die' },
    ]
  },
  'sneakers': {
    label: 'Sneaker Details',
    fields: [
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Nike, Adidas, New Balance' },
      { key: 'model', label: 'Model', placeholder: 'e.g. Air Jordan 1 Retro High OG' },
      { key: 'colorway', label: 'Colorway', placeholder: 'e.g. Chicago, Bred, Shadow' },
      { key: 'style_code', label: 'Style Code / SKU', placeholder: 'e.g. 555088-101' },
      { key: 'size_us', label: 'Size (US)', placeholder: 'e.g. 10, 10.5', inputMode: 'decimal' },
      { key: 'gender', label: 'Gender Sizing', type: 'select', options: ['Men\'s', 'Women\'s', 'Grade School', 'Toddler', 'Infant'] },
      { key: 'release_date', label: 'Release Date', placeholder: 'e.g. 2015, November 2019' },
      { key: 'deadstock', label: 'Deadstock (Unworn)', type: 'select', options: ['Yes – Deadstock', 'No – Worn', 'Display Worn Only'] },
      { key: 'box_included', label: 'Original Box', type: 'select', options: ['Yes – Original Box', 'Replacement Box', 'No Box'] },
    ]
  },
  'toys': {
    label: 'Toy / Figure Details',
    fields: [
      { key: 'brand', label: 'Brand / Manufacturer', placeholder: 'e.g. Hasbro, Kenner, NECA' },
      { key: 'line', label: 'Toy Line / Series', placeholder: 'e.g. Star Wars, G.I. Joe, Masters of the Universe' },
      { key: 'character', label: 'Character / Figure Name', placeholder: 'e.g. Darth Vader, Snake Eyes' },
      { key: 'scale', label: 'Scale / Size', placeholder: 'e.g. 3.75", 12", 1:6 scale' },
      { key: 'moc_mib', label: 'Packaging Status', type: 'select', options: ['MOC (Mint on Card)', 'MIB (Mint in Box)', 'Complete Loose', 'Incomplete Loose', 'Parts Only'] },
      { key: 'accessories_included', label: 'Accessories Included', placeholder: 'List included accessories' },
      { key: 'variant', label: 'Variant / Version', placeholder: 'e.g. First Release, Foreign, Running Change' },
    ]
  },
  'books': {
    label: 'Book Details',
    fields: [
      { key: 'author', label: 'Author', placeholder: 'e.g. J.R.R. Tolkien' },
      { key: 'publisher', label: 'Publisher', placeholder: 'e.g. Allen & Unwin' },
      { key: 'edition', label: 'Edition', placeholder: 'e.g. First Edition, First Printing' },
      { key: 'isbn', label: 'ISBN', placeholder: 'e.g. 978-0-618-00222-5' },
      { key: 'binding', label: 'Binding', type: 'select', options: ['Hardcover', 'Softcover / Paperback', 'Leather Bound', 'Spiral Bound', 'Other'] },
      { key: 'dust_jacket', label: 'Dust Jacket', type: 'select', options: ['Present – Fine', 'Present – Good', 'Present – Worn', 'Missing'] },
      { key: 'signed', label: 'Signed / Inscribed', type: 'select', options: ['No', 'Signed', 'Signed & Inscribed', 'Association Copy'] },
      { key: 'ex_library', label: 'Ex-Library Copy', type: 'select', options: ['No', 'Yes'] },
      { key: 'pages', label: 'Page Count', placeholder: 'e.g. 423', inputMode: 'numeric' },
    ]
  },
  'other': {
    label: 'Item Details',
    fields: [
      { key: 'brand', label: 'Brand / Maker', placeholder: 'Brand or manufacturer' },
      { key: 'model', label: 'Model / Type', placeholder: 'Model or type' },
      { key: 'material', label: 'Material', placeholder: 'e.g. Wood, Metal, Ceramic' },
      { key: 'dimensions', label: 'Dimensions / Size', placeholder: 'e.g. 10" × 5" × 3"' },
      { key: 'origin_country', label: 'Country of Origin', placeholder: 'e.g. Japan, USA' },
    ]
  }
};

const CONDITIONS = [
  { value: 'mint', label: 'Mint' },
  { value: 'near-mint', label: 'Near Mint' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'very-good', label: 'Very Good' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

// ─── Component ───────────────────────────────────────────────────────────────

const ItemForm = ({ item, collectionType, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    condition: 'good',
    purchase_price: '',
    current_value: '',
    estimated_value: '',
    description: '',
    notes: '',
    tags: [],
    rarity_score: '',
    is_featured: false,
    is_for_sale: false,
    // category-specific fields stored in specs object
    specs: {}
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');

  // Resolve category config
  const categoryConfig = CATEGORY_CONFIGS?.[collectionType] || CATEGORY_CONFIGS?.['other'];

  useEffect(() => {
    if (item) {
      // Extract known spec keys for this category
      const specKeys = categoryConfig?.fields?.map(f => f?.key) || [];
      const specs = {};
      specKeys?.forEach(key => {
        if (item?.[key] !== undefined && item?.[key] !== null) {
          specs[key] = String(item?.[key]);
        } else if (item?.specs?.[key] !== undefined) {
          specs[key] = String(item?.specs?.[key]);
        } else {
          specs[key] = '';
        }
      });

      setFormData({
        name: item?.name || '',
        condition: item?.condition || 'good',
        purchase_price: item?.purchase_price?.toString() || '',
        current_value: item?.current_value?.toString() || '',
        estimated_value: item?.estimated_value?.toString() || '',
        description: item?.description || '',
        notes: item?.notes || '',
        tags: item?.tags || [],
        rarity_score: item?.rarity_score?.toString() || '',
        is_featured: item?.is_featured || false,
        is_for_sale: item?.is_for_sale || false,
        specs
      });
    } else {
      // Initialize empty specs for this category
      const specKeys = categoryConfig?.fields?.map(f => f?.key) || [];
      const specs = {};
      specKeys?.forEach(key => { specs[key] = ''; });
      setFormData(prev => ({ ...prev, specs }));
    }
  }, [item, collectionType]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name?.trim()) {
      setError('Item name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Flatten specs into top-level fields for DB compatibility
      const specsFlat = {};
      Object.entries(formData?.specs || {})?.forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          specsFlat[k] = v;
        }
      });

      const submitData = {
        ...formData,
        ...specsFlat,
        specs: formData?.specs,
        purchase_price: formData?.purchase_price ? parseFloat(formData?.purchase_price) : null,
        current_value: formData?.current_value ? parseFloat(formData?.current_value) : null,
        estimated_value: formData?.estimated_value ? parseFloat(formData?.estimated_value) : null,
        rarity_score: formData?.rarity_score ? parseInt(formData?.rarity_score) : null
      };

      await onSave(submitData);
    } catch (err) {
      setError(err?.message || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      specs: { ...prev?.specs, [key]: value }
    }));
  };

  const addTag = () => {
    const tag = tagInput?.trim()?.toLowerCase();
    if (tag && !formData?.tags?.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...(prev?.tags || []), tag] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyPress = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      addTag();
    }
  };

  // ─── Render a single spec field ─────────────────────────────────────────
  const renderSpecField = (field) => {
    const value = formData?.specs?.[field?.key] ?? '';

    if (field?.type === 'select') {
      return (
        <div key={field?.key}>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            {field?.label}
          </label>
          <select
            value={value}
            onChange={(e) => handleSpecChange(field?.key, e?.target?.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Select…</option>
            {field?.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field?.key}>
        <label className="block text-sm font-medium text-card-foreground mb-2">
          {field?.label}
        </label>
        <Input
          value={value}
          onChange={(e) => handleSpecChange(field?.key, e?.target?.value)}
          placeholder={field?.placeholder || ''}
          inputMode={field?.inputMode}
        />
      </div>
    );
  };

  // ─── JSX ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">
            {item ? 'Edit Item' : 'Add New Item'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">

            {/* Error */}
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
                  <span className="text-sm text-destructive">{error}</span>
                </div>
              </div>
            )}

            {/* ── Basic Information ── */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-card-foreground">Basic Information</h3>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Item Name *
                </label>
                <Input
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  placeholder="Enter item name"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Year
                  </label>
                  <Input
                    type="number"
                    value={formData?.year || ''}
                    onChange={(e) => handleInputChange('year', e?.target?.value)}
                    placeholder="Year"
                    min="1"
                    max={new Date()?.getFullYear()}
                    inputMode="numeric"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Condition *
                  </label>
                  <select
                    value={formData?.condition}
                    onChange={(e) => handleInputChange('condition', e?.target?.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                    required
                  >
                    {CONDITIONS?.map(c => (
                      <option key={c?.value} value={c?.value}>{c?.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Describe the item…"
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
              </div>
            </div>

            {/* ── Category-Specific Specifications ── */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-card-foreground">
                {categoryConfig?.label}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categoryConfig?.fields?.map(renderSpecField)}
              </div>
            </div>

            {/* ── Pricing ── */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-card-foreground">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Purchase Price ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData?.purchase_price}
                    onChange={(e) => handleInputChange('purchase_price', e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    inputMode="decimal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Current Value ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData?.current_value}
                    onChange={(e) => handleInputChange('current_value', e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    inputMode="decimal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-2">
                    Estimated Value ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData?.estimated_value}
                    onChange={(e) => handleInputChange('estimated_value', e?.target?.value)}
                    placeholder="0.00"
                    min="0"
                    inputMode="decimal"
                  />
                </div>
              </div>
            </div>

            {/* ── Additional Details ── */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-card-foreground">Additional Details</h3>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Rarity Score (1–10)
                </label>
                <Input
                  type="number"
                  value={formData?.rarity_score}
                  onChange={(e) => handleInputChange('rarity_score', e?.target?.value)}
                  placeholder="Rate from 1–10"
                  min="1"
                  max="10"
                  inputMode="numeric"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Tags
                </label>
                <div className="flex items-center space-x-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e?.target?.value)}
                    onKeyPress={handleTagKeyPress}
                    placeholder="Add a tag"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addTag} iconName="Plus">
                    Add
                  </Button>
                </div>
                {formData?.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData?.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 text-primary hover:text-primary/70"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Notes
                </label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => handleInputChange('notes', e?.target?.value)}
                  placeholder="Additional notes…"
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData?.is_featured}
                    onChange={(e) => handleInputChange('is_featured', e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-background border border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <label htmlFor="is_featured" className="text-sm font-medium text-card-foreground">
                    Featured Item
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_for_sale"
                    checked={formData?.is_for_sale}
                    onChange={(e) => handleInputChange('is_for_sale', e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-background border border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <label htmlFor="is_for_sale" className="text-sm font-medium text-card-foreground">
                    Available for Sale
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading || !formData?.name?.trim()}
              iconName="Save"
              iconPosition="left"
            >
              {loading ? 'Saving…' : item ? 'Update Item' : 'Add Item'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemForm;